import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, output } from '@angular/core';
import type { FormControl } from '@angular/forms';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { GeoPluginService, phoneNumberValidatorFn } from '@core/common-legacy';
import { injectAuthTranslations, TwoFaService } from '@core/components-authorization-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  ButtonLoadingDirective,
  ButtonComponent,
  DialogFooterComponent,
  PhoneInputComponent,
  FormFieldComponent,
  LabelComponent,
  DialogBodyComponent,
  HeadingComponent,
  DialogHeaderComponent,
} from '@core/user-interface-legacy';

@Component({
  selector: 'rt-sms-activation-step-1',
  templateUrl: './sms-activation-step-1.component.html',
  styleUrls: ['./sms-activation-step-1.component.less'],
  imports: [
    DialogHeaderComponent,
    HeadingComponent,
    DialogBodyComponent,
    LabelComponent,
    FormFieldComponent,
    PhoneInputComponent,
    ReactiveFormsModule,
    InfoboxComponent,
    DialogFooterComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    TranslatePipe,
  ],
})
export class SmsActivationStep1Component implements OnInit, OnDestroy {
  public readonly translationKey = injectAuthTranslations();

  public readonly phoneErrors: { [key: string]: string } = {
    invalidNumber: this.i18n.translate(this.translationKey.dialog_2fa_sms.number_invalid),
    required: this.i18n.translate(this.translationKey.dialog_2fa_sms.number_required),
    maxlength: this.i18n.translate(this.translationKey.dialog_2fa_sms.number_length),
  };
  public phoneControl: FormControl<string>;
  public phoneApiError = false;
  public isLoading = false;
  public countryCode: string;
  public goNext = output<string>();
  public cancel = output<void>();

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private geoPlugin: GeoPluginService,
    private i18n: I18nService,
    private twoFaSmsService: TwoFaService<{ error?: string[]; errors?: string[] }>,
  ) {
    this.phoneControl = <FormControl<string>>(
      this.formBuilder.control('', [Validators.required, phoneNumberValidatorFn])
    );
  }

  public ngOnInit(): void {
    this.geoPlugin.fetch.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(data => {
      this.countryCode = data.country_code;
      this.cdRef.detectChanges();
    });

    this.phoneControl.valueChanges
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => this.cdRef.detectChanges());

    this.twoFaSmsService
      .getSmsSendSuccessAction()
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => {
        this.goNext.emit(this.phoneControl.value);
      });

    this.twoFaSmsService
      .getSmsSendFailAction()
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => {
        this.phoneApiError = true;
        this.isLoading = false;
        this.cdRef.detectChanges();
      });
  }

  public onContinueClick(): void {
    this.isLoading = true;
    this.phoneApiError = false;
    this.twoFaSmsService.sendSmsCode(this.phoneControl.value);

    this.cdRef.detectChanges();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public onCancelClick(): void {
    this.cancel.emit();
  }
}
